import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { SessionProvider } from "@/context/SessionContext";
import { ProtectedRoute, AdminRoute } from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import ScanPage from "@/pages/ScanPage";
import ScanResultPage from "@/pages/ScanResultPage";
import RegisterPage from "@/pages/RegisterPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";

// Protected/admin pages are behind a login gate and never need to be indexed or paint
// instantly for a public/crawler visit — lazy-loading them keeps the initial JS bundle
// (and therefore load time) small for the public, SEO-relevant pages above.
const BusinessProfilePage = lazy(() => import("@/pages/BusinessProfilePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const D3Page = lazy(() => import("@/pages/D3Page"));
const EvidenceUploadPage = lazy(() => import("@/pages/EvidenceUploadPage"));
const OpportunityPreviewPage = lazy(() => import("@/pages/OpportunityPreviewPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminBusinessDetailPage = lazy(() => import("@/pages/AdminBusinessDetailPage"));
const AdminSalesOutcomePage = lazy(() => import("@/pages/AdminSalesOutcomePage"));
const AdminSalesWorkspacePage = lazy(() => import("@/pages/AdminSalesWorkspacePage"));
const SalesOutcomePage = lazy(() => import("@/pages/SalesOutcomePage"));

const LazyFallback = () => <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading&hellip;</div>;

function App() {
  return (
    <AuthProvider>
      <SessionProvider>
        <BrowserRouter>
          <EmailVerificationBanner />
          <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/scan" element={<ScanPage />} />
              <Route path="/scan/result/:runId" element={<ScanResultPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/business-profile" element={<ProtectedRoute><BusinessProfilePage /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/d3" element={<ProtectedRoute><D3Page /></ProtectedRoute>} />
              <Route path="/evidence" element={<ProtectedRoute><EvidenceUploadPage /></ProtectedRoute>} />
              <Route path="/opportunity/:type" element={<ProtectedRoute><OpportunityPreviewPage /></ProtectedRoute>} />
              <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
              <Route path="/admin/businesses/:businessId" element={<AdminRoute><AdminBusinessDetailPage /></AdminRoute>} />
              <Route path="/admin/sales-outcome" element={<AdminRoute><AdminSalesOutcomePage /></AdminRoute>} />
              <Route path="/admin/sales-outcome/workspaces/:workspaceId" element={<AdminRoute><AdminSalesWorkspacePage /></AdminRoute>} />
              <Route path="/sales-outcome" element={<ProtectedRoute><SalesOutcomePage /></ProtectedRoute>} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
      </SessionProvider>
    </AuthProvider>
  );
}

export default App;
